import type { KITSU_DISCOVERY_EVENT_NAMES, KitsuContentChoice, KitsuStep } from "@kitsulab/schema";
import { apiClient } from "./apiClient";
import { ITEM_TEMPLATES } from "./itemTemplates";
import type { Item, Project } from "./types";

export type KitsuEventName =
	| (typeof KITSU_DISCOVERY_EVENT_NAMES)[number]
	| "kitsu_shown"
	| "kitsu_started"
	| "kitsu_dismissed"
	| "kitsu_resumed"
	| "kitsu_studio_question_shown"
	| "kitsu_studio_yes"
	| "kitsu_studio_no"
	| "kitsu_studio_download_clicked"
	| "kitsu_project_name_step_shown"
	| "kitsu_project_name_submitted"
	| "kitsu_project_created"
	| "kitsu_first_content_step_shown"
	| "kitsu_content_type_selected"
	| "kitsu_template_created"
	| "kitsu_create_button_highlight_shown"
	| "kitsu_plugin_step_shown"
	| "kitsu_plugin_store_clicked"
	| "kitsu_sync_step_shown"
	| "kitsu_linking_step_shown"
	| "kitsu_play_step_shown"
	| "kitsu_runtime_confirmed";

const TEMPLATE_BY_CHOICE: Record<KitsuContentChoice, string | null> = {
	npc: "npc",
	item: "weapon",
	blank: null,
};

export type KitsuEventInput = {
	eventName: KitsuEventName;
	step: KitsuStep;
	projectSlug?: string;
	properties?: { contentType?: KitsuContentChoice; studioInstalled?: boolean; errorCode?: string };
};

export async function updateKitsuProgress(input: KitsuEventInput): Promise<void> {
	await apiClient.post("/api/kitsu/events", input);
}

export function trackKitsuEvent(input: KitsuEventInput): Promise<void> {
	return updateKitsuProgress(input).catch(() => undefined);
}

export async function createKitsuProject(name: string): Promise<Project> {
	trackKitsuEvent({ eventName: "kitsu_project_name_submitted", step: "project" });
	const { data } = await apiClient.post<Project>("/api/projects", { name });
	trackKitsuEvent({ eventName: "kitsu_project_created", step: "project", projectSlug: data.slug });
	return data;
}

export async function createKitsuItem(projectSlug: string, choice: KitsuContentChoice, name: string): Promise<Item> {
	const properties = { contentType: choice };
	trackKitsuEvent({ eventName: "kitsu_content_type_selected", step: "content", projectSlug, properties });
	const templateKey = TEMPLATE_BY_CHOICE[choice];
	const template = ITEM_TEMPLATES.find((candidate) => candidate.key === templateKey);
	const { data } = await apiClient.post<Item>(`/api/projects/${projectSlug}/items`, {
		name, modelPaths: [], category: template?.itemCategory ?? null, graph: template?.graph() ?? null, folderId: null,
	});
	trackKitsuEvent({ eventName: "kitsu_template_created", step: "content", projectSlug, properties });
	return data;
}
